import React, { useState } from "react";
import { RefreshCw, Sparkles, X } from "lucide-react";
import { seoApi, SeoApiError } from "../../services/seoApi";
import type { GenerateResult, SeoMarket, SeoSwitches } from "./types";

interface Props {
  market: SeoMarket;
  switches: SeoSwitches | null;
  onClose: () => void;
  onDone: (result: GenerateResult) => void;
}

const MODES: Array<{ id: "auto" | "news" | "evergreen"; label: string; help: string }> = [
  { id: "auto", label: "Automatisk", help: "Nyheder først (score ≥ 75), ellers næste evergreen-hul." },
  { id: "news", label: "Kun nyheder", help: "Skriver kun, hvis en nyhed fra de sidste 72 timer scorer ≥ 75." },
  { id: "evergreen", label: "Evergreen", help: "Næste godkendte idé eller søgeord uden indhold." },
];

export default function GenerateArticleDialog({ market, switches, onClose, onDone }: Props) {
  const [mode, setMode] = useState<"auto" | "news" | "evergreen">("auto");
  const [keyword, setKeyword] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const llmOff = switches?.llm !== "live";

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await seoApi.generate(market, { mode, keyword: keyword.trim() || undefined });
      setResult(res);
      onDone(res);
    } catch (e) {
      setError(e instanceof SeoApiError ? e.message : "Kaldet fejlede");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-gray-100">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0F3F34]" />
            <span>Generér artikel nu ({market})</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Luk">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-gray-600 leading-relaxed">
          Agenten scorer dagens finansnyheder og skriver enten en nyhedskommentar eller en evergreen-guide med
          Claude Opus 5.5 på AWS Bedrock. Tabeller, interne links og lovpligtige bokse lægges på af koden. Udkastet
          lander i <strong>Til review</strong> og udgives først, når en person har godkendt det.
        </p>

        {llmOff && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            SEO_LLM_MODE er slået fra i Lambdaen. Agenten træffer beslutningen og gemmer den, men skriver ingen tekst
            (beløbssider bygges dog uden model).
          </div>
        )}

        <div className="space-y-2">
          {MODES.map((m) => (
            <label key={m.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer text-xs ${
              mode === m.id ? "border-[#0F3F34] bg-[#0F3F34]/5" : "border-gray-200 hover:bg-gray-50"}`}>
              <input type="radio" name="mode" className="mt-0.5" checked={mode === m.id} onChange={() => setMode(m.id)} />
              <span>
                <span className="font-bold text-gray-900 block">{m.label}</span>
                <span className="text-gray-500">{m.help}</span>
              </span>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Bestemt søgeord (valgfrit)</label>
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
                 placeholder={market === "NO" ? "f.eks. refinansiering med betalingsanmerkning" : "f.eks. samla lån med betalningsanmärkning"}
                 className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
          <p className="text-[11px] text-gray-400 mt-1">Et søgeord springer nyhedstrinnet over og skriver en guide til netop det.</p>
        </div>

        {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs">{error}</div>}

        {result && (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1">
            <div><strong>Beslutning:</strong> {result.decision?.path ?? "–"} · {result.decision?.reason}</div>
            {result.content_id ? (
              <div><strong>Udkast:</strong> #{result.content_id} ({result.content_type}) ligger nu i Til review.</div>
            ) : (
              <div className="text-gray-600">{result.reason || result.error || "Intet udkast skrevet."}</div>
            )}
            {result.llm && (
              <div className="text-gray-500">
                {result.llm.input_tokens?.toLocaleString("da-DK")} input- / {result.llm.output_tokens?.toLocaleString("da-DK")} output-tokens
                · ca. ${Number(result.llm.cost_usd_estimate || 0).toFixed(2)}
              </div>
            )}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2 text-xs">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50">
            Luk
          </button>
          <button onClick={run} disabled={running}
                  className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl disabled:opacity-50 flex items-center gap-1.5">
            {running ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Skriver… (1–3 min)</> : <>Start agenten</>}
          </button>
        </div>
      </div>
    </div>
  );
}
